import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeRevenueCat, getCustomerInfo, hasActiveSubscription } from '../lib/revenuecat';

// Type definitions for RevenueCat (simplified for web)
interface CustomerInfo {
  entitlements: {
    active: { [key: string]: any };
  };
}

interface Offerings {
  current: any;
}

interface RevenueCatContextType {
  isInitialized: boolean;
  customerInfo: CustomerInfo | null;
  offerings: Offerings | null;
  hasActiveSubscription: boolean;
  isLoading: boolean;
  refreshCustomerInfo: () => Promise<void>;
}

const RevenueCatContext = createContext<RevenueCatContextType | undefined>(undefined);

export const useRevenueCat = () => {
  const context = useContext(RevenueCatContext);
  if (context === undefined) {
    throw new Error('useRevenueCat must be used within a RevenueCatProvider');
  }
  return context;
};

interface RevenueCatProviderProps {
  children: React.ReactNode;
}

export const RevenueCatProvider: React.FC<RevenueCatProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [offerings, setOfferings] = useState<Offerings | null>(null);
  const [hasActiveSub, setHasActiveSub] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refreshCustomerInfo = async () => {
    try {
      const info = await getCustomerInfo();
      setCustomerInfo(info);
      
      // Check for premium entitlement (you'll need to configure this in RevenueCat dashboard)
      const hasPremium = await hasActiveSubscription('premium');
      setHasActiveSub(hasPremium);
    } catch (error) {
      console.error('Error refreshing customer info:', error);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      try {
        const initialized = await initializeRevenueCat();
        setIsInitialized(initialized);
        
        if (initialized) {
          await refreshCustomerInfo();
        }
      } catch (error) {
        console.error('Error initializing RevenueCat:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, []);

  const value: RevenueCatContextType = {
    isInitialized,
    customerInfo,
    offerings,
    hasActiveSubscription: hasActiveSub,
    isLoading,
    refreshCustomerInfo,
  };

  return (
    <RevenueCatContext.Provider value={value}>
      {children}
    </RevenueCatContext.Provider>
  );
}; 